import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArchiveRestore, RotateCcw, Trash2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Pantry trash recovery dialog. Self-contained: loads trash items on open,
 * handles per-item restore/purge and bulk restore-all / empty-trash.
 * Emits `onChanged()` after every mutation so the parent can refresh its pantry list.
 */
export default function PantryTrashDialog({ open, onOpenChange, onChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.listPantryTrash();
        setItems(res.data.items || []);
      } catch {
        toast.error("Kunne ikke laste papirkurv");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const restoreOne = async (id) => {
    try {
      await api.restorePantryTrashItem(id);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Gjenopprettet");
      onChanged?.();
    } catch { toast.error("Kunne ikke gjenopprette"); }
  };

  const purgeOne = async (id) => {
    if (!window.confirm("Slette denne varen permanent? Kan ikke angres.")) return;
    try {
      await api.purgePantryTrashItem(id);
      setItems((p) => p.filter((i) => i.id !== id));
      toast.success("Slettet permanent");
    } catch { toast.error("Feil ved sletting"); }
  };

  const restoreAll = async () => {
    try {
      const res = await api.restoreAllPantryTrash();
      toast.success(`${res.data.restored} varer gjenopprettet`);
      setItems([]);
      onOpenChange(false);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  const emptyAll = async () => {
    if (!window.confirm("Tømme hele papirkurven permanent? Kan ikke angres.")) return;
    try {
      const res = await api.emptyPantryTrash();
      toast.success(`${res.data.purged} varer slettet permanent`);
      setItems([]);
    } catch (err) { toast.error(err.response?.data?.detail || "Feil"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pantry-trash-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Papirkurv</DialogTitle>
          <DialogDescription>
            Slettede varer ligger her i 30 dager og kan gjenopprettes. Etter 30 dager fjernes de automatisk.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {loading ? (
            <div className="text-center py-8 text-stone-500 text-sm">Laster…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-10 text-stone-500 text-sm" data-testid="pantry-trash-empty">
              <ArchiveRestore className="h-10 w-10 mx-auto mb-2 text-stone-300" />
              Papirkurven er tom
            </div>
          ) : (
            <>
              <div className="flex gap-2 pb-3 border-b border-stone-200">
                <button
                  onClick={restoreAll}
                  className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 transition-colors"
                  data-testid="restore-all-trash-btn"
                >
                  <RotateCcw className="h-4 w-4" /> Gjenopprett alle ({items.length})
                </button>
                <button
                  onClick={emptyAll}
                  className="inline-flex items-center gap-2 rounded-md border border-red-200 bg-white px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="empty-trash-btn"
                >
                  <Trash2 className="h-4 w-4" /> Tøm papirkurv
                </button>
              </div>
              <ul className="divide-y divide-stone-100">
                {items.map((it) => {
                  const d = it.trashed_at ? new Date(it.trashed_at) : null;
                  const daysAgo = d ? Math.floor((Date.now() - d.getTime()) / 86400000) : 0;
                  const daysLeft = Math.max(0, 30 - daysAgo);
                  return (
                    <li key={it.id} className="py-2.5 flex items-center gap-3" data-testid={`trash-item-${it.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-stone-900 truncate">{it.name}</div>
                        <div className="text-xs text-stone-500">
                          {it.category || "Annet"} · {it.amount || ""} {it.unit || ""}
                          {d && <span className="ml-2">· slettet {daysAgo === 0 ? "i dag" : `${daysAgo} dag${daysAgo === 1 ? "" : "er"} siden`} · {daysLeft} dager igjen</span>}
                        </div>
                      </div>
                      <button
                        onClick={() => restoreOne(it.id)}
                        className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                        data-testid={`restore-trash-${it.id}`}
                        title="Gjenopprett"
                      >
                        <RotateCcw className="h-3.5 w-3.5" /> Gjenopprett
                      </button>
                      <button
                        onClick={() => purgeOne(it.id)}
                        className="p-1.5 rounded-md text-stone-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                        data-testid={`purge-trash-${it.id}`}
                        title="Slett permanent"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
